import { Link } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { useApplyBrand } from "@/hooks/use-apply-brand";
import { resolveActiveMembership, useMyCustomerContext } from "@/hooks/use-customer-context";
import { useDocumentTitle } from "@/hooks/use-document-title";
import { DEFAULT_BRAND_COLOR } from "@/lib/brand";
import { homeLink } from "@/lib/destinations";
import { readRememberedPortal } from "@/lib/portal-memory";
import { useCustomerStore } from "@/store";
import { AppFrame } from "@/components/shell/app-frame";
import { AppHeader } from "@/components/shell/app-header";
import { NeutralFrame } from "@/components/shell/neutral-frame";
import { BottomBar } from "@/components/ui/bottom-bar";
import { buttonClasses } from "@/components/ui/button";

/**
 * What the root renders for every path no route serves, in its own frame, since
 * no layout renders around it: the signed-in frame for a signed-in visitor, the
 * neutral frame otherwise. No destination owns a missing page, so nothing on it
 * is marked as the current page; its links match exactly, so the router never
 * marks one because the missing path sits under it.
 */
export function NotFound() {
  const signedIn = useCustomerStore((s) => s.isAuthenticated);
  return signedIn ? <SignedInNotFound /> : <SignedOutNotFound />;
}

/**
 * The signed-in frame, branded and titled the way the signed-in layout does
 * it. The header waits for `/me` to name the utility and the person.
 */
function SignedInNotFound() {
  const { t } = useTranslation("customer");
  const contextQuery = useMyCustomerContext();
  const activeAccountId = useCustomerStore((s) => s.activeAccountId);
  const memberships = contextQuery.data?.memberships ?? [];
  const active = resolveActiveMembership(memberships, activeAccountId);
  const profile = contextQuery.data?.profile;

  const remembered = readRememberedPortal()?.branding;
  useApplyBrand(active?.brand_color ?? remembered?.brand_color);
  useDocumentTitle(active?.org_name ?? null);

  return (
    <AppFrame
      header={
        active && profile ? (
          <AppHeader
            memberships={memberships}
            active={active}
            profile={profile}
            destination={null}
          />
        ) : null
      }
      bottomBar={<BottomBar active={null} />}
    >
      <section className="max-w-[420px] space-y-4 rounded-card bg-surface p-5 shadow-card lg:p-7">
        <h1 className="text-xl font-semibold tracking-[-0.01em] text-ink">{t("notFound.title")}</h1>
        <Link
          {...homeLink()}
          activeOptions={{ exact: true }}
          className={buttonClasses("secondary")}
        >
          {t("notFound.home")}
        </Link>
      </section>
    </AppFrame>
  );
}

/**
 * The neutral frame, branded and titled the way the bare sign-in page does
 * it, with one way onward: the sign-in, which forwards to a remembered utility
 * by itself. It never reads the signed-in context.
 */
function SignedOutNotFound() {
  const { t } = useTranslation("customer");
  useApplyBrand(DEFAULT_BRAND_COLOR);
  useDocumentTitle(null);

  return (
    <NeutralFrame>
      <div className="flex flex-col items-center gap-4">
        <h1 className="max-w-[36ch] text-center text-lg text-ink">{t("notFound.title")}</h1>
        <Link to="/login" activeOptions={{ exact: true }} className={buttonClasses("secondary")}>
          {t("notFound.signIn")}
        </Link>
      </div>
    </NeutralFrame>
  );
}
